/* eslint-disable @typescript-eslint/ban-ts-comment */
import { subMinutes, isAfter, parse } from 'date-fns'
import { nanoid, customAlphabet } from 'nanoid'

import { SPACE } from '../constants'

export type SomeObject = Record<string, unknown>

/**
 * Faster way to decode JWT
 * @param token - JWT token
 */
export function parseJWT(token: string): Record<string, string> | null {
  try {
    if (typeof token !== 'string') return null

    const [, data] = token.split('.')
    return JSON.parse(atob(data || SPACE))
  } catch (e) {
    return null
  }
}

export function isPlainObject<T>(value: T) {
  return value && typeof value === 'object' && value !== null && value?.constructor === Object
}

export function timestamp(): number {
  return new Date().getTime()
}

export const wrap = <T, K>(fn: Promise<T | void>): Promise<K | T | void> => new Promise(resolve => {
  return fn ? fn.then(resolve).catch(() => resolve(undefined)) : resolve(undefined)
})

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export const crypto = (typeof window !== 'undefined') ? (window.crypto || window.msCrypto) : null
// @ts-ignore
export const subtle = crypto ? (crypto.subtle || crypto.webkitSubtle) : null

export const isIE = (): boolean => (window.navigator.userAgent.match(/MSIE|Trident/) !== null)

/**
 *
 * @param buffer - ArrayBuffer
 */
export function arrayBufferToString(buffer: ArrayBuffer | SharedArrayBuffer | ArrayLike<number>): string {
  const charCode = new Uint8Array(buffer)
  return String.fromCharCode.apply(null, charCode as unknown as number[])
}

export function stringToArrayBuffer(string: string): ArrayBuffer {
  const buffer = new ArrayBuffer(string.length)
  const array = new Uint8Array(buffer)

  for (let i = 0, length = string.length; i < length; i++) {
    array[i] = string.charCodeAt(i)
  }

  return buffer
}

export function encode(message: string): ArrayBuffer {
  if (!window.TextEncoder) return new Uint8Array(stringToArrayBuffer(message))
  return new TextEncoder().encode(message)
}

export function decode(bytes: ArrayBuffer): string {
  //@ts-ignore
  if (!window.TextDecoder) return String.fromCharCode.apply(null, new Uint8Array(bytes))
  return new TextDecoder('utf-8').decode(bytes)
}

/**
 *
 * @param delay - How many miliseconds to wait
 */
export function wait(delay: number): Promise<void> {
  return new Promise(resolve => {
    setTimeout(resolve, delay)
  })
}

let jsonpFuncKey = 0

/**
 *
 * @param url - Your base URL
 * @param options - JSONP options
 */
export function jsonp<T>(url: string, options: Record<string, string | number> = {}): Promise<T> {
  return new Promise(function (resolve, reject) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const object = (options.object || window) as any
    const key: string = options.key as string || 'nn' + jsonpFuncKey++
    const parameter = 'parameter' in options ? options.parameter : 'callback'
    const SECOND = 1000

    const script: HTMLScriptElement = document.createElement('script')
    script.type = 'application/javascript'
    script.async = true
    script.onload = () => setTimeout(reject, 8 * SECOND)
    script.onerror = () => {
      delete object[key as never]
      reject(new Error('JSONP script load error'))
    }

    object[key] = (response: T) => {
      delete object[key as never]
      resolve(response)
    }

    script.src = parameter ? (url + (~url.indexOf('?') ? '&' : '?') + parameter + '=' + key) : url // eslint-disable-line no-implicit-coercion

    document.head.removeChild(document.head.appendChild(script))
  })
}

export const isObject = isPlainObject

export const preloadImage = (src: string): PromiseLike<Event | Error | unknown> => {
  return new Promise((resolve, reject) => {
    const image = new Image
    image.onload = resolve
    image.onerror = reject
    image.src = src
  })
}

export const isEmptyObject = <T>(object: T): boolean => object && isObject(object) && Object.entries(object).length === 0

export const isValidDate = (date: Date): boolean => date instanceof Date && !isNaN(date.getTime())

export function isCallable(target: unknown): boolean {
  return !!target &&
    typeof target === 'function' &&
    Function.prototype.call === target.call &&
    Function.prototype.apply === target.apply
}

export const createAudioContext = (): AudioContext => window.AudioContext ?
  new window.AudioContext :
  // @ts-ignore
  window['webkitAudioContext'] as AudioContext ?
    // @ts-ignore
    new window['webkitAudioContext'] as AudioContext :
    (() => { throw new Error('HTML5 Audio is not available!')})()

/**
 * MP3 (Lossy) - IE 9-11, Edge+, Firefox, Chrome, Safari
 * MPEG(M4a) (Lossy) - IE 9-11, Edge+, Firefox, Chrome, Safari
 * MP4 (Lossy) - IE 9-11, Edge+, Firefox, Chrome, Safari
 * Ogg (Lossy) - Edge 17+, Firefox, Chrome (IE, Edge 12+, Safari - not supported)
 * AAC (Lossy) -  IE 9-11, Edge+, Firefox(mp4 only), Chrome, Safari
 * Wav (Lossless) - Edge+, Firefox, Chrome, Safari (IE 6-11 not supported)
 * FLAC (Lossless) - Edge 16+, Firefox, Chrome, Safari
 *
 */
export function isLosslessSupported(format: string | null = null): boolean {
  const audio = new Audio
  const lossless = ['audio/flac', 'audio/wav', 'audio/aiff']
    .filter(mime => format ? mime.includes(format) : true)
  if (audio instanceof HTMLAudioElement) {
    return lossless.some(mime => audio.canPlayType(mime) !== SPACE)
  }
  return false
}

export function isLossySupported(format: string | null = null): boolean {
  format = format === 'm4a' ? 'mpeg' : format
  const audio = new Audio
  const lossy = ['audio/mp3', 'audio/mpeg', 'audio/aac', 'audio/ogg']
    .filter(mime => format ? mime.includes(format) : true)
  if (audio instanceof HTMLAudioElement) {
    return lossy.some(mime => audio.canPlayType(mime) !== SPACE)
  }
  return false
}

/**
 *
 * @param time - Format time in seconds to string 'HH?:MM:SS'
 */
export const formatTime = (time: number, leadingZero = true): string => {
  time = Number(time)
  if (time >= 0) {
    const hours = Math.floor(time / 3600)
    const minutes = Math.floor(time / 60) % 60
    const seconds = Math.round(time % 60)

    return [hours, minutes, seconds]
      .filter((v, i) => i > 0 ? true : v > 0)
      .map((v, i) => {
        const l = String(v).length === 1
        if (i === 0 && l) {
          return leadingZero ? `0${v}` : v
        } else if (l) {
          return `0${v}`
        }
        return v
      }).join(':')
  }

  return leadingZero ? '00:00' : '0:00'
}

export const isSafari = /Safari/i.test(navigator.userAgent)
  && /Apple Computer/.test(navigator.vendor) && !/Mobi|Android/i.test(navigator.userAgent)

export const isFirefox = () => /firefox/i.test(navigator?.userAgent || '')

/**
 * Fix legacy CDN base urls.
 * @param link - streamlink
 */
export const fixCDNBaseUrl = (link: string): string =>{
  if (typeof link !== 'string') return link
  return link
    .replace('http://nugslpmobile.edgesuite.net', 'https://nugslpmobile.akamaized.net')
    .replace('http://nugs2.edgesuite.net', 'https://securestream02.akamaized.net')
    .replace('http://nugs10.edgesuite.net', 'https://nugs10.akamaized.net')
    .replace('http://nugsuniversal-f.akamaihd.net', 'https://nugsuniversal-f.akamaihd.net')
}

export const emptyAudio = 'data:audio/wav;base64,UklGRjIAAABXQVZFZm10IBIAAAABAAEAQB8AAEAfAAABAAgAAABmYWN0BAAAAAAAAABkYXRhAAAAAA=='

export function fixSafariAudioBug(): void {
  if (isSafari) {
    const handler = () => {
      const audio = new Audio
      audio.muted = true
      audio.src = emptyAudio
      audio.play()
      document.body.removeEventListener('click', handler)
    }
    document.body.addEventListener('click', handler)
  }
}

export const flags = new Map

export function generatePrivateMembers<T extends string>(keys: T[]): { [K in T]: symbol } {
  return keys.reduce((props, propName) => {
    props[propName] = Symbol(`#${propName}`)
    return props
  }, Object.create(null))
}

export function swing(p: number): number {
  return 0.5 - Math.cos(p * Math.PI) / 2
}

export const getTimeOnTimeline = (duration: number) => (event: MouseEvent, target: HTMLElement): void => {
  if (!target) return

  const { left } = target.getBoundingClientRect()
  const { x } = event
  const percentage = Math.round(-(left - x) / Number(target.clientWidth) * 100)
  const time = formatTime(percentage * duration / 100)

  target.title = time
}

export const isAsyncFn = (fn: (...args: unknown[]) => unknown): boolean => {
  return !!(fn instanceof Promise ||
    (typeof fn === 'function' && typeof fn['then' as never] === 'function' && fn[Symbol.toStringTag as never] === 'Promise') ||
    (fn && fn.constructor.name == 'AsyncFunction'))
}

export function queryParams(): Record<string, string> {
  const output = Object.create(null)
  const query = new URLSearchParams(location.search)

  query.forEach((value, key) => output[key] = value)

  return output
}

export function parseBoolean(value: string): boolean | null {
  try {
    return Boolean(JSON.parse(String(value)))
  } catch {
    return null
  }
}

export function isTokenExpired(expiredAt: Date): boolean {
  const now = new Date()
  const safeExpirationDate = subMinutes(expiredAt, 2)

  return isAfter(now, safeExpirationDate)
}

export function LOG<T>(...args: T[]): void {
  window['console']['log'](`%c SDK:${String(args[0]).toUpperCase()} `, 'color: #6a00b0; border-radius: 1px; border: 1px solid #6a00b0; font-size: 12px', ...args.splice(1))
}

type NetworkPerformanceEstimateRange = 0|0.1|0.2|0.3|0.4|0.5|0.6|0.7|0.8|0.9|1

export function preloadEstimate(): NetworkPerformanceEstimateRange {
  const DEFAULT_PRELOAD_ESTIMATE = 1 as NetworkPerformanceEstimateRange

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const connection: {effectiveType: string} = navigator['connection' as never] || navigator['mozConnection' as never] || navigator['webkitConnection' as never]

  if (connection && connection['effectiveType']) {
    switch (connection['effectiveType']) {
      case 'slow-2g':
        return 0.1
      case '2g':
        return 0.2
      case '3g':
        return 0.3
      case '4g':
        return 0.7
      default:
        return DEFAULT_PRELOAD_ESTIMATE
    }
  } else {
    return DEFAULT_PRELOAD_ESTIMATE
  }
}


export function cacheCDNRequests(domains: string[]): void {
  domains.forEach(domain => {
    const link = document.createElement('link')
    link.href = domain
    link.rel = 'preconnect'
    document.head.appendChild(link)
    const link2 = link.cloneNode() as HTMLLinkElement
    link2.rel = 'dns-prefetch'
    document.head.appendChild(link2)
  })
}

export function dateToUTCStamp(date: string, format: string): number {
  const d = parse(date, format, new Date)
  const offset = d.getTimezoneOffset() * 60

  return (d.getTime() / 1000) - offset
}

export function parseLinkExtension(link: string): string {
  try {
    return new URL(link).pathname.split('.').pop() as string
  } catch {
    return SPACE
  }
}
export function range(start: number, stop = 0, step = 1) {
  if (stop === 0) {
    stop = start
    start = 0
  }

  return {
    *[Symbol.iterator]() {
      for (let n = start; n < stop; n += step) yield n
    }
  }
}

export function uuid() {
  const alphabet = '0123456789abcdef'
  const v4 = customAlphabet(alphabet, 36)
  const indexes = [8, 13, 18, 23]
  const symbols = Array.from(v4())
  indexes.forEach(index => symbols[index] = '-')
  return symbols.join(SPACE)
}

export { nanoid }

export function preload(url: string) {
  return new Promise((resolve, reject) => {
    const link = document.createElement('link')
    link.rel = 'prefetch'
    link.as = 'script'
    link.setAttribute('crossorigin', SPACE)
    link.addEventListener('load', resolve)
    link.addEventListener('error', reject)
    link.href = url
    document.head.appendChild(link)
  })
}

export function load<T>(src: string, key: string): Promise<T> {
  return new Promise((resolve, reject) => {
    if (key && window[key as never] != null) return resolve(window[key as never] as T)
    const script = document.createElement('script')
    script.async = true
    script.onload = () => resolve(window[key as never] as T)
    script.onerror = () => reject()
    script.crossOrigin = SPACE
    script.src = src

    document.head.removeChild(document.head.appendChild(script))
  })
}

export function getSitename() {
  const q = new URLSearchParams(location.search)
  const origin = q.get('origin') as string

  if (origin) {
    return origin
  }

  const ancestorOrigin = Array.isArray(document.location.ancestorOrigins)
    ? document.location.ancestorOrigins[document.location.ancestorOrigins.length - 1]
    : ''

  return document.referrer && document.referrer.startsWith('http')
    ? new URL(ancestorOrigin || document.referrer).hostname
    : ancestorOrigin || location.hostname
}

export const isDarkMode = () => window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
